import React from 'react';
import { Table, Button, Space, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { DispatchDeliverySummaryRecord } from '../graphql/queries/getDispatchDeliverySummary';
import type { DispatchDeliverySummaryDimension } from '../hooks/useDispatchDeliverySummary';
import dayjs from 'dayjs';
import { useShouldUseVerticalLayout } from '../../../../../utils/languageUtils';
import TablePaginationBar from '../../../../../components/TablePaginationBar';

interface Props {
    data: DispatchDeliverySummaryRecord[];
    loading: boolean;
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number, size?: number) => void;
    dspExpanded: boolean;
    whsExpanded: boolean;
    onToggleDsp: () => void;
    onToggleWhs: () => void;
    dimension: DispatchDeliverySummaryDimension;
}

const formatRate = (value?: number) => {
    if (value === undefined || value === null) {
        return '--';
    }
    return `${value}%`;
};

// 计算妥投率颜色
// 规则：<30% 红色；30% <= rate < 70% 黄色；70% <= rate < 100% 蓝色；=100% 绿色
const getDeliveryRateColor = (rate: number): string => {
    if (rate === 100) {
        return '#52c41a'; // 绿色
    } else if (rate >= 70) {
        return '#1890ff'; // 蓝色
    } else if (rate >= 30) {
        return '#faad14'; // 黄色
    } else {
        return '#ff4d4f'; // 红色
    }
};

const DispatchDeliverySummaryTable: React.FC<Props> = ({
    data,
    loading,
    page,
    pageSize,
    total,
    onPageChange,
    dspExpanded,
    whsExpanded,
    onToggleDsp,
    onToggleWhs,
    dimension
}) => {
    const { t } = useTranslation();

    const renderDateCell = (record: DispatchDeliverySummaryRecord) => {
        if (dimension === 'DAY') {
            return record.statDate ? dayjs(record.statDate).format('YYYY-MM-DD') : '-';
        }
        if (dimension === 'WEEK') {
            const weekDisplay = record.statWeek || '-';
            const displayText = record.weekDateRange 
                ? `${weekDisplay} (${record.weekDateRange})`
                : weekDisplay;
            
            // 如果未满一周，字体颜色改为深橙色（区别于妥投率的黄色）
            if (record.isIncompleteWeek) {
                return <span style={{ color: '#ff6b00', fontWeight: 500 }}>{displayText}</span>;
            }
            return displayText;
        }
        if (dimension === 'MONTH') {
            const monthDisplay = record.statMonth || '-';
            const displayText = record.monthDateRange 
                ? `${monthDisplay} (${record.monthDateRange})`
                : monthDisplay;
            
            // 如果未满一月，字体颜色改为深橙色（区别于妥投率的黄色）
            if (record.isIncompleteMonth) {
                return <span style={{ color: '#ff6b00', fontWeight: 500 }}>{displayText}</span>;
            }
            return displayText;
        }
        return '-';
    };

    const getDateColumnTitle = () => {
        if (dimension === 'WEEK') return t('wms.delivery.dispatchDeliverySummary.columns.week');
        if (dimension === 'MONTH') return t('wms.delivery.dispatchDeliverySummary.columns.month');
        return t('wms.delivery.dispatchDeliverySummary.columns.date');
    };

    // 判断是否应该显示T+N妥投率
    // 规则：
    // - t0: 当前自然日期 >= 统计日期时，显示数据
    // - t1-t3: 当前自然日期 >= 统计日期 + N天时，显示数据
    // 举例：统计日期10-31日，t0为10-31日当天，t1为11-01日，t2为11-02日，以此类推
    const shouldShowTNDeliveryRate = (record: DispatchDeliverySummaryRecord, n: number): boolean => {
        const statDate = record.statDate;
        if (!statDate) {
            return false;
        }
        const statDateObj = dayjs(statDate).startOf('day'); // 统计日期的00:00:00
        const currentDate = dayjs().startOf('day'); // 当前自然日期的00:00:00
        
        if (n === 0) {
            // t0: 当前日期 >= 统计日期时显示
            return currentDate.isSameOrAfter(statDateObj);
        } else {
            // t1-t3: 当前日期 >= 统计日期 + N天时显示
            const targetDate = statDateObj.add(n, 'day').startOf('day');
            return currentDate.isSameOrAfter(targetDate);
        }
    };

    // 渲染T+N妥投率（带日期判断）
    const renderTNDeliveryRate = (value: any, record: DispatchDeliverySummaryRecord, n: number) => {
        const shouldShow = shouldShowTNDeliveryRate(record, n);
        
        if (!shouldShow) {
            return <span>-</span>;
        }

        let rate: number | undefined;
        let displayText: string;
        if (typeof value === 'string') {
            displayText = `${value}%`;
            rate = parseFloat(value);
        } else {
            displayText = formatRate(value);
            rate = value;
        }
        if (rate !== undefined && rate !== null && !isNaN(rate)) {
            const color = getDeliveryRateColor(rate);
            return <span style={{ color }}>{displayText}</span>;
        }
        return displayText;
    };

    // 创建带 Tooltip 的表头
    const createColumnTitleWithTooltip = (columnKey: string) => {
        const title = t(`wms.delivery.dispatchDeliverySummary.columns.${columnKey}`);
        const definition = t(`wms.delivery.dispatchDeliverySummary.definitions.${columnKey}`);
        return (
            <Space>
                <span>{title}</span>
                <Tooltip title={definition}>
                    <QuestionCircleOutlined style={{ color: '#1890ff', cursor: 'help' }} />
                </Tooltip>
            </Space>
        );
    };

    const useVerticalLayout = useShouldUseVerticalLayout();

    const columns: ColumnsType<any> = [
        {
            title: getDateColumnTitle(),
            dataIndex: 'statDate',
            width: 200,
            align: 'left',
            ellipsis: false,
            render: (_, record) => <span style={{ whiteSpace: 'nowrap' }}>{renderDateCell(record)}</span>
        },
        {
            title: (
                <Space>
                    <span>{t('wms.delivery.dispatchDeliverySummary.columns.dspName')}</span>
                    <Button
                        type="text"
                        size="small"
                        onClick={onToggleDsp}
                        style={{
                            backgroundColor: '#f9f0ff',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            minWidth: 'auto',
                            height: 'auto',
                            fontSize: '12px',
                            color: '#722ed1',
                            fontWeight: 'bold'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#efdbff';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#f9f0ff';
                        }}
                    >
                        {dspExpanded ? t('delivery.tasks.message.collapse') : t('delivery.tasks.message.expand')}
                    </Button>
                </Space>
            ),
            width: 280,
            align: 'left',
            ellipsis: false,
            dataIndex: 'dspName',
            render: (value, record) => {
                if (!dspExpanded) {
                    return '-';
                }
                if (!value) {
                    return record.dspId > 0 ? `#${record.dspId}` : '-';
                }
                return <span style={{ whiteSpace: useVerticalLayout ? 'normal' : 'nowrap' }}>{value || `#${record.dspId}`}</span>;
            }
        },
        {
            title: (
                <Space>
                    <span>{t('wms.delivery.dispatchDeliverySummary.columns.warehouseName')}</span>
                    <Button
                        type="text"
                        size="small"
                        onClick={onToggleWhs}
                        style={{
                            backgroundColor: '#f9f0ff',
                            borderRadius: '4px',
                            padding: '2px 6px',
                            minWidth: 'auto',
                            height: 'auto',
                            fontSize: '12px',
                            color: '#722ed1',
                            fontWeight: 'bold'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#efdbff';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#f9f0ff';
                        }}
                    >
                        {whsExpanded ? t('delivery.tasks.message.collapse') : t('delivery.tasks.message.expand')}
                    </Button>
                </Space>
            ),
            width: 300,
            align: 'left',
            ellipsis: false,
            dataIndex: 'warehouseName',
            render: (value, record) => {
                if (!whsExpanded) {
                    return '-';
                }
                if (value) {
                    return <span style={{ whiteSpace: useVerticalLayout ? 'normal' : 'nowrap' }}>{value}</span>;
                }
                return record.warehouseId > 0 ? `#${record.warehouseId}` : '-';
            }
        },
        {
            title: createColumnTitleWithTooltip('actualArrivalsCount'),
            dataIndex: 'actualArrivalsCount',
            width: 250,
            align: 'left',
            ellipsis: false,
            render: (value) => <span style={{ whiteSpace: 'nowrap' }}>{value}</span>
        },
        {
            title: createColumnTitleWithTooltip('t0DeliveredRate'),
            dataIndex: 't0DeliveredRate',
            width: 200,
            align: 'left',
            ellipsis: false,
            render: (value, record) => renderTNDeliveryRate(value, record, 0)
        },
        {
            title: createColumnTitleWithTooltip('t1DeliveredRate'),
            dataIndex: 't1DeliveredRate',
            width: 200,
            align: 'left',
            ellipsis: false,
            render: (value, record) => renderTNDeliveryRate(value, record, 1)
        },
        {
            title: createColumnTitleWithTooltip('t2DeliveredRate'),
            dataIndex: 't2DeliveredRate',
            width: 200,
            align: 'left',
            ellipsis: false,
            render: (value, record) => renderTNDeliveryRate(value, record, 2)
        },
        {
            title: createColumnTitleWithTooltip('t3DeliveredRate'),
            dataIndex: 't3DeliveredRate',
            width: 200,
            align: 'left',
            ellipsis: false,
            render: (value, record) => renderTNDeliveryRate(value, record, 3)
        }
    ];

    return (
        <>
            <Table
                rowKey="id"
                dataSource={data}
                columns={columns}
                loading={loading}
                scroll={{ x: 'max-content' }}
                pagination={false}
                size="middle"
                tableLayout="auto"
             className="mb-4"/>
            <TablePaginationBar
                total={total}
                current={page}
                pageSize={pageSize}
                onChange={onPageChange}
            />
        </>
    );
};

export default DispatchDeliverySummaryTable;


